import type { EntryValueViewT } from "@patient-watch/types";
import { resolveInputKey } from "@/features/pages/utils/inputKeyAliases";

/** Legacy chartable input keys when a form has no chart_order metadata. */
const CHARTABLE_LEGACY_INPUT_KEYS = ["number", "pain-slider", "slider", "single-choice"] as const;

function isEntryValueChartAligned(ev: EntryValueViewT, allEntryValues: EntryValueViewT[]): boolean {
    const formId = ev.form_id;
    const siblings =
        formId != null && formId !== ""
            ? allEntryValues.filter(
                  (v) => v.form_id === formId || v.form_id == null || v.form_id === undefined || v.form_id === ""
              )
            : allEntryValues;

    const hasChartOrderMeta = siblings.some((s) => s.chart_order != null);
    if (hasChartOrderMeta) {
        return ev.chart_order != null || ev.is_primary_score === true;
    }

    return CHARTABLE_LEGACY_INPUT_KEYS.includes(ev.input_key as (typeof CHARTABLE_LEGACY_INPUT_KEYS)[number]);
}

import { formatLocaleDate, DEFAULT_LOCALE, normaliseLocaleTag } from "@patient-watch/i18n";
import { getDiaryCopy } from "@/features/i18n/diaryCopy";

/** Placeholder when a free-text answer was not filled in. */
export const BLANK_TEXT_PLACEHOLDER = "Left blank";

export function formatTextEntryValue(value: string | null | undefined, locale?: string): string {
    if (value == null || String(value).trim() === "") {
        const resolvedLocale = normaliseLocaleTag(locale) ?? DEFAULT_LOCALE;
        return getDiaryCopy(resolvedLocale).blankAnswer;
    }
    return String(value);
}

/** @deprecated Use formatLocaleDate from @patient-watch/i18n */
export function formatEntryDate(value: Date | string | number | null | undefined, locale = "en-GB"): string {
    return formatLocaleDate(value, locale, { day: "numeric", month: "short", year: "numeric" });
}

/** Whether an entry value maps to a form page input (editable) vs a calculated score (page_id null). */
export function isEditableEntryValue(ev: EntryValueViewT): boolean {
    return ev.page_index != null;
}

/** Whether an entry value row should appear in history / entry cards. */
export function shouldDisplayEntryValue(ev: EntryValueViewT): boolean {
    if (resolveInputKey(ev.input_key ?? "") === "file") {
        return Boolean(ev.object_id);
    }

    if (ev.input_key === "long-text") {
        return true;
    }

    const value = ev.value;
    if (value === undefined || value === null || value === "undefined") {
        return ev.number_value !== null && ev.number_value !== undefined;
    }

    return true;
}

/**
 * Calculated / headline scores (no form page).
 * Uses chart metadata when present (WOMAC subscales have chart_order 1–3; total has is_primary_score).
 * Falls back to legacy number inputs without a page — do not rely on input_index === 0 alone.
 */
export function isCalculatedScoreEntryValue(ev: EntryValueViewT, allEntryValues: EntryValueViewT[] = []): boolean {
    if (ev.page_index != null) return false;
    if (ev.input_key === "warning") return true;
    return isEntryValueChartAligned(ev, allEntryValues.length > 0 ? allEntryValues : [ev]);
}

/** Headline scores sorted for display: primary total first, then subscales by chart_order. */
export function sortHeadlineEntryValues(entryValues: EntryValueViewT[]): EntryValueViewT[] {
    return [...entryValues].sort((a, b) => {
        const keyA = headlineSortKey(a);
        const keyB = headlineSortKey(b);
        if (keyA !== keyB) return keyA - keyB;
        return (a.input_label ?? "").localeCompare(b.input_label ?? "");
    });
}

function headlineSortKey(ev: EntryValueViewT): number {
    if (ev.is_primary_score && ev.chart_order == null) return 0;
    if (ev.chart_order != null) return ev.chart_order;
    return 9_999;
}

/** Per-page questionnaire answers (excludes calculated scores). */
export function isPageAnswerEntryValue(ev: EntryValueViewT): boolean {
    return ev.page_index != null;
}

export interface EntryValuesHistoryPartition {
    headlineValues: EntryValueViewT[];
    questionValues: EntryValueViewT[];
    /** When true, render all values in one list (no headline vs questions split). */
    showAllWithoutSplit: boolean;
}

/**
 * Split entry values for history cards: calculated scores as headlines when present,
 * otherwise show every answer in one list (no chart-style promotion of number inputs).
 */
export function partitionEntryValuesForHistoryDisplay(entryValues: EntryValueViewT[]): EntryValuesHistoryPartition {
    const calculatedScores = sortHeadlineEntryValues(
        entryValues.filter((ev) => isCalculatedScoreEntryValue(ev, entryValues))
    );
    if (calculatedScores.length > 0) {
        return {
            headlineValues: calculatedScores,
            questionValues: entryValues.filter(isPageAnswerEntryValue),
            showAllWithoutSplit: false,
        };
    }
    return {
        headlineValues: [],
        questionValues: entryValues,
        showAllWithoutSplit: true,
    };
}
